import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ServiceClient } from '../../Services/ServiceClient';
import TeamHeader from './Components/TeamHeader/TeamHeader';
import ActiveAgents from './Components/ActiveAgents/ActiveAgents';
import { isModalTypeCorrect, modalType } from './OpenedModalType';
import BankAccountLog from './Components/Modals/BankAccountLog/BankAccountLog';
import EditAgentBankAccount from './Components/Modals/EditAgentBankAccount';
import AgentInfo from './Components/Modals/AgentInfo';
import RevokeConfirmation from './Components/Modals/RevokeConfirmation';
import CopyCostCenters from './Components/Modals/CopyCostCenters';
import DefaultAgentConfirmation from './Components/Modals/DefaultAgentConfirmation';
import TeamInfo from './Components/Modals/TeamInfo';
import { areDecoupledFunctionsAvailableFunction, mapGetTeamResponse, mapLocationStateToTeam } from './Functions';
import HistoricalAgents from './Components/HistoricalAgents';
import { formatMessage } from '../../Localization/formatMessage';
import Messages from '../../Localization/Messages';
import './Styles.css';
import AddressLog from './Components/Modals/AddressLog';
import { PresentationMode } from '../../Enums/PresentationMode';
import { useGlobalContext } from '../../context/GlobalContext';
import LoaderComponent from '../../Elements/ids/Loader';
import { ResultErrorCode } from '../../Enums/ResultErrorCode';
import ErrorMessage from '../../Elements/ErrorMessage';
import { createServerErrorMessage } from '../../Helpers/FormInputHelper';
import TeamNotFound from './Components/TeamNotFound';
import AddressInfo from './Components/Modals/AddressInfo';
import { teamPageReducer, teamPageReducerTypes } from './Reducer';
import BreadcrumbRow from '../../Elements/Breadcrumbs';
import { getActiveAgent } from '../../Helpers/AgentHelper';
import LinkError from './Components/LinkError';
import TeamHierarchy from './Components/Modals/TeamHierarchy/TeamHierarchy';

const TeamPageContext = createContext();
export const useTeamPageContext = () => useContext(TeamPageContext);

const TeamPage = ({ presentationMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addBreadcrumbs } = useGlobalContext();
  const { codePrefix, modal, agentCode } = useParams();

  const [pageState, teamPageDispatch] = useReducer(teamPageReducer, {
    pageMode: presentationMode === PresentationMode.Add ? presentationMode : PresentationMode.Loading,
    team: mapLocationStateToTeam(location.state),
    openedModal: presentationMode === PresentationMode.Add ? modalType.TeamInfo : modalType.None,
    modalAgent: {},
    errorData: {},
  });

  const [areHistoricalAgentsOpen, setAreHistoricalAgentsOpen] = useState(false);

  useEffect(() => {
    if (pageState.pageMode === PresentationMode.Add) {
      addBreadcrumbs([
        {
          trackingName: 'teamPage',
          link: `/team/add/${codePrefix}`,
          title: formatMessage(Messages.breadcrumb_teamPage),
        },
      ]);
    } else {
      addBreadcrumbs([
        {
          trackingName: 'teamPage',
          link: `/team/view/${codePrefix}`,
          title: formatMessage(Messages.breadcrumb_teamPage),
        },
      ]);
    }

    if (pageState.pageMode !== PresentationMode.Add) {
      ServiceClient.getTeam(
        codePrefix,
        data => {
          const mappedTeam = mapGetTeamResponse(data);

          if (modal !== null && modal !== undefined) {
            if (!isModalTypeCorrect(modal)) {
              teamPageDispatch({
                type: teamPageReducerTypes.deepLinkErrorResult,
                payload: {
                  team: mappedTeam,
                  errorData: {
                    code: ResultErrorCode.DeepLinkIncorrect,
                    message: formatMessage(Messages.teamPage_notSupportedModalType),
                  },
                },
              });
              return;
            }

            if (agentCode !== null && agentCode !== undefined) {
              const agent = getActiveAgent(mappedTeam, agentCode);

              if (agent === null) {
                teamPageDispatch({
                  type: teamPageReducerTypes.deepLinkErrorResult,
                  payload: {
                    team: mappedTeam,
                    errorData: {
                      code: ResultErrorCode.DeepLinkIncorrect,
                      message: formatMessage(Messages.teamPage_agentNotFound, { agentCode: agentCode }),
                    },
                  },
                });
                return;
              }

              teamPageDispatch({
                type: teamPageReducerTypes.setOpenedModal,
                payload: {
                  modalType: modal,
                  modalAgent: agent,
                },
              });
            } else {
              teamPageDispatch({
                type: teamPageReducerTypes.setOpenedModal,
                payload: {
                  modalType: modal,
                  modalAgent: {},
                },
              });
            }
          }

          teamPageDispatch({
            type: teamPageReducerTypes.foundTeamResult,
            payload: mappedTeam,
          });
        },
        (errorMessage, errorResponse) => {
          teamPageDispatch({
            type: teamPageReducerTypes.setErrorData,
            payload: errorResponse,
          });
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const areDecoupledFunctionsAvailable = areDecoupledFunctionsAvailableFunction(pageState.team);
  const areBrokerFunctionsAvailable = !areDecoupledFunctionsAvailable;

  const closeModalView = () => {
    teamPageDispatch({
      type: teamPageReducerTypes.setOpenedModal,
      payload: {
        modalType: modalType.None,
      },
    });

    if (modal) {
      navigate(`/team/view/${codePrefix}`, { replace: true });
    }
  };

  return (
    <TeamPageContext.Provider
      value={{
        pageState,
        teamPageDispatch,
      }}>
      <div className="teamPage-container">
        <BreadcrumbRow />

        {pageState.pageMode === PresentationMode.Loading ? (
          <LoaderComponent />
        ) : pageState.pageMode === PresentationMode.Error ? (
          <>
            {pageState.errorData.code === ResultErrorCode.TeamNotFound ? (
              <TeamNotFound codePrefix={pageState.errorData.argument} />
            ) : pageState.errorData.code === ResultErrorCode.DeepLinkIncorrect ? (
              <LinkError errorMessage={pageState.errorData.message} codePrefix={codePrefix} />
            ) : (
              <ErrorMessage message={createServerErrorMessage(pageState.errorData.message).message} />
            )}
          </>
        ) : (
          <>
            <TeamHeader team={pageState.team} />

            <div className="teamPage-innerContainer">
              <ActiveAgents
                team={pageState.team}
                areBrokerFunctionsAvailable={areBrokerFunctionsAvailable}
                teamPageDispatch={teamPageDispatch}
              />

              {pageState.pageMode !== PresentationMode.Add && (
                <>
                  <div
                    className="teamPage_hideHistoricalAgents clickable"
                    onClick={() => {
                      setAreHistoricalAgentsOpen(!areHistoricalAgentsOpen);
                    }}>
                    {areHistoricalAgentsOpen
                      ? formatMessage(Messages.teamPage_hideHistoricalAgents)
                      : formatMessage(Messages.teamPage_showHistoricalAgents)}
                  </div>

                  {areHistoricalAgentsOpen && <HistoricalAgents team={pageState.team} />}
                </>
              )}
            </div>

            {pageState.openedModal === modalType.AgentInfo ? (
              <AgentInfo
                agent={pageState.modalAgent}
                team={pageState.team}
                teamPageDispatch={teamPageDispatch}
                closeView={closeModalView}
                areBrokerFunctionsAvailable={areBrokerFunctionsAvailable}
                doesTeamExist={pageState.pageMode === PresentationMode.Add ? false : true}
              />
            ) : pageState.openedModal === modalType.BankAccount ? (
              <EditAgentBankAccount
                closeView={closeModalView}
                agentCode={pageState.modalAgent?.code}
                teamBankAccount={pageState.team.bankAccount}
              />
            ) : pageState.openedModal === modalType.BankLog ? (
              <BankAccountLog
                agentCode={pageState.modalAgent?.code}
                codePrefix={pageState.team.codePrefix}
                closeView={closeModalView}
              />
            ) : pageState.openedModal === modalType.Commission ? (
              <Navigate
                to={{
                  pathname: `/team/commission/${pageState.modalAgent?.code}`,
                }}
              />
            ) : pageState.openedModal === modalType.Revoke ? (
              <RevokeConfirmation
                agent={pageState.modalAgent}
                closeView={closeModalView}
                team={pageState.team}
                teamPageDispatch={teamPageDispatch}
              />
            ) : pageState.openedModal === modalType.AddressInfo ? (
              <AddressInfo
                agentCode={pageState.modalAgent.code}
                codePrefix={pageState.team.codePrefix}
                closeView={closeModalView}
              />
            ) : pageState.openedModal === modalType.AddressLog ? (
              <AddressLog
                agentCode={pageState.modalAgent.code}
                codePrefix={pageState.team.codePrefix}
                closeView={closeModalView}
              />
            ) : pageState.openedModal === modalType.CopyCostCenter ? (
              <CopyCostCenters team={pageState.team} closeView={closeModalView} teamPageDispatch={teamPageDispatch} />
            ) : pageState.openedModal === modalType.DefaultAgent ? (
              <DefaultAgentConfirmation
                agent={pageState.modalAgent}
                closeView={closeModalView}
                team={pageState.team}
                teamPageDispatch={teamPageDispatch}
              />
            ) : pageState.openedModal === modalType.TeamInfo ? (
              <TeamInfo team={pageState.team} teamPageDispatch={teamPageDispatch} closeView={closeModalView} />
            ) : pageState.openedModal === modalType.TeamHierarchy ? (
              <TeamHierarchy team={pageState.team} closeView={closeModalView} />
            ) : (
              <></>
            )}
          </>
        )}
      </div>
    </TeamPageContext.Provider>
  );
};

export default TeamPage;
